import React, { useCallback, useEffect, useState } from 'react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch as SwitchComp,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';

import EmojiObjectsTwoToneIcon from '@material-ui/icons/EmojiObjectsTwoTone';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CameraAltIcon from '@material-ui/icons/CameraAlt';

import { CustomButton } from '@/components/atoms/CustomButton';
import Input from '@/components/atoms/Input';
import Tooltip from '@/components/atoms/Tooltip';
import Modal from '@/components/atoms/Modal';
import { WithIconButton } from '@/components/atoms/Buttons';
import Drawer from '@/components/atoms/Drawer';

import { ModuleFormData } from '@/components/organisms/ModuleForm';

import { useAgent } from '@/hooks';

import { ModulesRouteParams } from '@/routes/ClientRoute';

import companiesRequest from '@/api/companies';

import { ModuleWithCameras } from '@/slicers/cameras';
import {
  switchMaxAmount,
  switchOrigin,
  unitMaxAmount,
  unitOrigin,
} from '@/config';
import { Switch, Units } from '@/slicers/modules';
import { User } from '@/slicers/users';

import Calendar from '../Calendar';
import CameraDrawer from '../CameraDrawer';
import TestConnectPopup from '../TestConnectPopup';
import SwitchCheckbox from '../SwitchCheckbox';
import RemarkTextField from '@/components/atoms/RemarkTextField';

import classes from './styles.module.scss';

const createUnit = (id: number, unitNumber: number): Units => ({
  id: id,
  name: unitNumber.toString(),
  number: unitNumber,
  switchesByNumber: {},
});

const createSwitch = (
  id: number,
  switchNumber: number,
  switchName: string
): Switch => ({
  id: id,
  name: switchName,
  number: switchNumber,
  shutter_mode: 0,
  icon: 'light',
});
// 4x4=16スイッチになるように足りなければ足す(id=0の場合はhiddenとする)
const createModuleWith = (_moduleWith?: ModuleWithCameras) => {
  let moduleWith: ModuleFormData = _moduleWith
    ? JSON.parse(JSON.stringify({ ..._moduleWith, deleted_camera_ids: [] }))
    : {
        device_code: '',
        company_id: 0,
        id: 1,
        name: '',
        remark: '',
        site_id: 0,
        unitsByNumber: {},
        cameras: [],
        deleted_camera_ids: [],
      };

  const id = Object.keys(moduleWith.unitsByNumber).length ? 0 : 1;

  for (
    let unitNumber = unitOrigin;
    unitNumber < unitMaxAmount + unitOrigin;
    unitNumber++
  ) {
    if (!moduleWith.unitsByNumber[unitNumber]) {
      // ユニットがなければ作る
      moduleWith.unitsByNumber[unitNumber] = createUnit(id, unitNumber);
    }
    for (
      let switchNumber = switchOrigin;
      switchNumber < switchMaxAmount + switchOrigin;
      switchNumber++
    ) {
      const switchesByNumber =
        moduleWith.unitsByNumber[unitNumber].switchesByNumber;
      if (!switchesByNumber[switchNumber]) {
        //   スイッチがなければ作る
        switchesByNumber[switchNumber] = createSwitch(
          id,
          switchNumber,
          `${unitMaxAmount - unitNumber}${switchNumber}`
        );
      }
    }
  }

  return moduleWith;
};

type B16ChannelSettingsProps = {
  showOverlay: () => void;
  hideOverlay: () => void;
  commModule: ModuleWithCameras;
  loginUser: User;
};

const B16ChannelSettings = ({
  loginUser,
  commModule,
  showOverlay,
  hideOverlay,
}: B16ChannelSettingsProps) => {
  const isMobile = useAgent();
  const history = useHistory();

  const [isOpenCameraForm, setOpenCameraForm] = useState(false);
  const [isOpenRemark, setOpenRemark] = useState(false);
  const { module: moduleId } = useParams<ModulesRouteParams>();

  const dispatch = useDispatch();
  const { register, handleSubmit, errors, control } = useForm<ModuleFormData>();

  const [moduleWith, setModuleWith] = useState(
    createModuleWith(commModule || undefined)
  );

  const [remark, setRemark] = useState(moduleWith.remark ?? '');
  const onBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleClickVisibility = (unitNumber: number, switchNumber: number) => {
    setModuleWith((_state) => {
      const state: ModuleFormData = JSON.parse(JSON.stringify(_state));

      const powerSwitch =
        state.unitsByNumber[unitNumber].switchesByNumber[switchNumber];

      if (powerSwitch.id !== 0) {
        powerSwitch.id = 0;
        powerSwitch.icon = '';
        powerSwitch.shutter_mode = 0;
      } else {
        powerSwitch.id = 1;
        powerSwitch.icon = 'light';
      }

      return state;
    });
  };

  const handleChangeShutterMode = (
    unitNumber: number,
    switchNumber: number
  ) => {
    setModuleWith((_state) => {
      const state: ModuleFormData = JSON.parse(JSON.stringify(_state));

      const powerSwitch =
        state.unitsByNumber[unitNumber].switchesByNumber[switchNumber];
      powerSwitch.shutter_mode = +!powerSwitch.shutter_mode as 1 | 0;

      return state;
    });
  };

  const handleLockMode = (unitNumber: number, switchNumber: number) => {
    setModuleWith((_state) => {
      const state: ModuleFormData = JSON.parse(JSON.stringify(_state));

      const powerSwitch =
        state.unitsByNumber[unitNumber].switchesByNumber[switchNumber];
      powerSwitch.is_lock = +!powerSwitch.is_lock as 1 | 0;

      return state;
    });
  };

  const handleLeakageCheck = (unitNumber: number, switchNumber: number) => {
    setModuleWith((_state) => {
      const state: ModuleFormData = JSON.parse(JSON.stringify(_state));

      const powerSwitch =
        state.unitsByNumber[unitNumber].switchesByNumber[switchNumber];
      powerSwitch.leakage_check = !powerSwitch.leakage_check;

      return state;
    });
  };

  const handleRequiredConfirmation = (
    unitNumber: number,
    switchNumber: number
  ) => {
    setModuleWith((_state) => {
      const state: ModuleFormData = JSON.parse(JSON.stringify(_state));

      const powerSwitch =
        state.unitsByNumber[unitNumber].switchesByNumber[switchNumber];
      powerSwitch.required_confirmation = !powerSwitch.required_confirmation;

      return state;
    });
  };

  const handleChangeIconType = (
    unitNumber: number,
    switchNumber: number,
    value: string
  ) => {
    setModuleWith((_state) => {
      const state: ModuleFormData = JSON.parse(JSON.stringify(_state));

      const powerSwitch =
        state.unitsByNumber[unitNumber].switchesByNumber[switchNumber];
      powerSwitch.icon = value;

      return state;
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    const {
      start_date,
      due_date,
      unitsByNumber = [],
      earthquake_intensity,
      ...other
    } = data;
    let moduleWith: any = {
      ...other,
      earthquake_intensity:
        earthquake_intensity != null ? +earthquake_intensity : undefined,
      start_date: start_date ? moment(start_date).format('YYYY-MM-DD') : null,
      due_date: due_date ? moment(due_date).format('YYYY-MM-DD') : null,
      units: [],
      remark: remark
    };

    Object.values(unitsByNumber).forEach((powerUnit) => {
      const { switchesByNumber, ...other } = powerUnit;
      moduleWith.units.push({
        ...other,
        switches: Object.values(switchesByNumber)
          .filter((sw) => sw.id && sw.id.toString() !== '0')
          .map((sw) => ({
            ...sw,
            is_lock: +!!sw.is_lock as 1 | 0,
          })),
      });
    });

    showOverlay();
    const api = companiesRequest.reregister(
      loginUser!.company_id.toString(),
      moduleId
    );
    await dispatch(api.request(moduleWith));
    hideOverlay();
  });

  useEffect(() => {
    if (commModule) {
      const moduleWith = createModuleWith(commModule);
      setModuleWith(moduleWith);
    }
    // eslint-disable-next-line
  }, [JSON.stringify(commModule)]);

  const addCameraButton = (
    <WithIconButton
      icon={CameraAltIcon}
      color="info"
      className={classes.addButton}
      onClick={() => setOpenCameraForm(true)}
    >
      カメラ作成
    </WithIconButton>
  );

  const shouldSummaryChecked = Object.values(moduleWith.unitsByNumber).some(
    (item) => {
      return Object.values(item.switchesByNumber).some((sw) => sw.shutter_mode);
    }
  );

  const shouldSummaryLocked = Object.values(moduleWith.unitsByNumber).some(
    (item) => {
      return Object.values(item.switchesByNumber).some((sw) => sw.is_lock);
    }
  );

  return (
    <>
      <form className={classes.b16ChannelSettings} onSubmit={onSubmit}>
        <div className={classes.heading}>
          <div className={classes.title}>
            {commModule.name}​​​​​​​​の基本情報
          </div>
          <div className={classes.commonInfo}>
            <div className={classes.inputs}>
              <div className={classes.input}>
                <Input
                  readonly
                  name="device_code"
                  defaultValue={moduleWith.device_code}
                  label="デバイスコード"
                  labelType="block"
                  ref={register({
                    required: true,
                  })}
                  tooltip={<div>デバイスコード</div>}
                />
              </div>
              <div className={classes.input}>
                <Input
                  maxLength={20}
                  name="name"
                  defaultValue={moduleWith.name?.substr(0, 20)}
                  label="指令盤の名"
                  labelType="block"
                  tooltip={<div>指令盤の名</div>}
                  ref={register({
                    required: true,
                  })}
                />
              </div>
              <div className={classes.input}>
                <Input
                  name="control_no"
                  ref={register({})}
                  defaultValue={moduleWith.control_no || ''}
                  label="管理番号"
                  labelType="block"
                  tooltip={<div>管理番号</div>}
                />
              </div>
            </div>
            <div className={[classes.inputs, classes.checkboxes].join(' ')}>
              <div>{!isMobile && addCameraButton}</div>
              <div>
                <div className={classes.errorMessage}>
                  {errors.name && 'name is required.'}
                </div>
              </div>
              <Controller
                defaultValue={
                  moduleWith.start_date ? new Date(moduleWith.start_date) : null
                }
                control={control}
                name="start_date"
                as={({ value, onChange }) => {
                  return <Calendar 
                            title={'開始予定日'} 
                            value={value} 
                            onChange={onChange} 
                          />;
                }}
              />
              <Controller
                defaultValue={
                  moduleWith.due_date ? new Date(moduleWith.due_date) : null
                }
                control={control}
                name="due_date"
                as={({ value, onChange }) => {
                  return <Calendar 
                            title={'終了予定日'}
                            value={value} 
                            onChange={onChange} 
                          />;
                }}
              />
            {(loginUser.realType && (loginUser.realType <= 200)) &&
                <CustomButton
                  color="primary"
                  onClick={() => {
                    setOpenRemark(true);
                  }}
                >
                  備考
                </CustomButton>
            }
            </div>
          </div>
          {isMobile && addCameraButton}
        </div>
        <div className={classes.switchDetail}>
          <div className={classes.notePanel}>
            <div>
              <div className={classes.panelTitle}>ボタンの設定編集</div>
              <div className={classes.notes}>
                <div className={classes.note}>
                  <div className={classes.noteHeading}>
                    <FormControlLabel
                      className={classes.switch}
                      control={<SwitchComp checked color="primary" />}
                      label="表示"
                      labelPlacement="start"
                    />
                  </div>
                  <div className={classes.noteDes}>
                    <span>操作盤へ表示されるかを設定できます。</span>
                  </div>
                </div>
                <div className={classes.note}>
                  <div className={classes.noteHeading}>
                    <div className={classes.whiteBox}>スイッチ名</div>
                  </div>
                  <div className={classes.noteDes}>
                    <span>スイッチ名を自由に指定できます。</span>
                  </div>
                </div>
                <div className={classes.note}>
                  <div className={classes.noteHeading}>
                    <div className={classes.shadowBox}>デザイン選択</div>
                  </div>
                  <div className={classes.noteDes}>
                    <span>ボタンのデザインデザインを選択できます。</span>
                  </div>
                </div>
              </div>
            </div>
            {!isMobile && (
              <div className={classes.panelFooter}>
                <CustomButton
                  onClick={onBack}
                  className={classes.backButton}
                  color="grey"
                >
                  戻る
                </CustomButton>
              </div>
            )}
          </div>
          <div className={classes.switchPanel}>
            <div className={classes.switches}>
              {Array.from({ length: unitMaxAmount }).map((_, i) => {
                const unitNumber = unitMaxAmount - i;
                const powerUnit = moduleWith.unitsByNumber[unitNumber];

                return (
                  <div key={i} className={classes.unit}>
                    <input
                      name={`unitsByNumber[${powerUnit.number}].number`}
                      defaultValue={powerUnit.number}
                      ref={register({ required: true })}
                      hidden
                    />
                    <SwitchCheckbox
                      label="グループボタン表示"
                      {...{
                        defaultChecked: !!powerUnit.group_on_off_flg,
                        size: 'small',
                        name: `unitsByNumber[${powerUnit.number}].group_on_off_flg`,
                        inputRef: register({}),
                      }}
                    />
                    {Object.values(powerUnit.switchesByNumber).map(
                      (powerSwitch, j) => {
                        const switchName = `unitsByNumber[${powerUnit.number}].switchesByNumber[${powerSwitch.number}]`;

                        /// defaultValueで表示するpower_meter_id (nullの時は””を表示したい)
                        // const defaultPwMeterId = powerSwitch.power_meter_id
                        //   ? powerSwitch.power_meter_id
                        //   : '';

                        return (
                          <div key={j} className={classes.switchBox}>
                            <div>
                              <input
                                type="hidden"
                                name={`${switchName}.id`}
                                ref={register()}
                                defaultValue={powerSwitch.id}
                              />
                              <input
                                type="hidden"
                                name={`${switchName}.state`}
                                ref={register()}
                                defaultValue={powerSwitch.state}
                              />
                              <div className={classes.boxTop}>
                                <FormControlLabel
                                  className={classes.visibleSwitch}
                                  control={
                                    <SwitchComp
                                      size="small"
                                      checked={powerSwitch.id !== 0}
                                      color="primary"
                                      onClick={(event) =>
                                        handleClickVisibility(
                                          powerUnit.number,
                                          powerSwitch.number
                                        )
                                      }
                                    />
                                  }
                                  label="表示"
                                  labelPlacement="start"
                                />
                                <div className={classes.boxTopRight}>
                                  <SwitchCheckbox
                                    label="異常メール受信"
                                    color="#000"
                                    disabled={!moduleWith.leakage_check}
                                    {...{
                                      // eslint-disable-next-line
                                      checked:
                                        !!moduleWith.leakage_check &&
                                        !!powerSwitch.leakage_check,
                                      name: `${switchName}.leakage_check`,
                                      inputRef: register({}),
                                      onClick: () =>
                                        handleLeakageCheck(
                                          powerUnit.number,
                                          powerSwitch.number
                                        ),
                                    }}
                                  />
                                  <SwitchCheckbox
                                    label="ロック"
                                    color="#000"
                                    {...{
                                      // eslint-disable-next-line
                                      checked: powerSwitch.is_lock == 1,
                                      name: `${switchName}.is_lock`,
                                      inputRef: register({}),
                                      onClick: () =>
                                        handleLockMode(
                                          powerUnit.number,
                                          powerSwitch.number
                                        ),
                                    }}
                                  />
                                  <SwitchCheckbox
                                    label="操作確認"
                                    color="#000"
                                    {...{
                                      checked:
                                        // eslint-disable-next-line
                                        powerSwitch.required_confirmation ==
                                        true,
                                      name: `${switchName}.required_confirmation`,
                                      inputRef: register({}),
                                      onClick: () =>
                                        handleRequiredConfirmation(
                                          powerUnit.number,
                                          powerSwitch.number
                                        ),
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            <input
                              key={powerSwitch.name}
                              name={`${switchName}.name`}
                              ref={register()}
                              defaultValue={powerSwitch.name}
                              className={classes.switchName}
                            />
                            <input
                              hidden
                              name={`${switchName}.number`}
                              ref={register({ required: true })}
                              defaultValue={powerSwitch.number}
                            />
                            <Tooltip
                              trigger={
                                <div className={classes.shadowButton}>
                                  <div>
                                    {!powerSwitch.icon ? (
                                      'デザイン選択'
                                    ) : powerSwitch.icon === 'light' ? (
                                      <EmojiObjectsTwoToneIcon />
                                    ) : powerSwitch.icon === 'up' ? (
                                      <ArrowDropUpIcon
                                        className={classes.small}
                                      />
                                    ) : powerSwitch.icon === 'down' ? (
                                      <ArrowDropDownIcon
                                        className={classes.small}
                                      />
                                    ) : (
                                      powerSwitch.icon === 'stop' && (
                                        <div className={classes.stopIcon} />
                                      )
                                    )}
                                  </div>
                                  <input
                                    ref={register({})}
                                    name={`${switchName}.icon`}
                                    type="hidden"
                                    value={powerSwitch.icon || ''}
                                  />
                                </div>
                              }
                            >
                              <div className={classes.switchPopup}>
                                <div className={classes.title}>
                                  ボタンのデザインを選択して下さい
                                </div>
                                <RadioGroup>
                                  <div className={classes.iconOptions}>
                                    <FormControlLabel
                                      data-tooltip-role="close"
                                      className={classes.iconOption}
                                      control={
                                        <Radio
                                          checked={!powerSwitch.icon}
                                          onChange={() => {
                                            handleChangeIconType(
                                              powerUnit.number,
                                              powerSwitch.number,
                                              ''
                                            );
                                          }}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <div className={classes.optionHolder}>
                                          <div />
                                        </div>
                                      }
                                      labelPlacement="bottom"
                                    />
                                    <FormControlLabel
                                      data-tooltip-role="close"
                                      className={classes.iconOption}
                                      control={
                                        <Radio
                                          checked={powerSwitch.icon === 'light'}
                                          onChange={() => {
                                            handleChangeIconType(
                                              powerUnit.number,
                                              powerSwitch.number,
                                              'light'
                                            );
                                          }}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <div className={classes.optionHolder}>
                                          <div>
                                            <EmojiObjectsTwoToneIcon />
                                          </div>
                                        </div>
                                      }
                                      labelPlacement="bottom"
                                    />
                                    <FormControlLabel
                                      data-tooltip-role="close"
                                      className={classes.iconOption}
                                      control={
                                        <Radio
                                          checked={powerSwitch.icon === 'up'}
                                          onChange={() => {
                                            handleChangeIconType(
                                              powerUnit.number,
                                              powerSwitch.number,
                                              'up'
                                            );
                                          }}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <div className={classes.optionHolder}>
                                          <div>
                                            <ArrowDropUpIcon fontSize="large" />
                                          </div>
                                        </div>
                                      }
                                      labelPlacement="bottom"
                                    />
                                    <FormControlLabel
                                      data-tooltip-role="close"
                                      className={classes.iconOption}
                                      control={
                                        <Radio
                                          checked={powerSwitch.icon === 'stop'}
                                          onChange={() => {
                                            handleChangeIconType(
                                              powerUnit.number,
                                              powerSwitch.number,
                                              'stop'
                                            );
                                          }}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <div className={classes.optionHolder}>
                                          <div>
                                            <div className={classes.stopIcon} />
                                          </div>
                                        </div>
                                      }
                                      labelPlacement="bottom"
                                    />
                                    <FormControlLabel
                                      data-tooltip-role="close"
                                      className={classes.iconOption}
                                      control={
                                        <Radio
                                          checked={powerSwitch.icon === 'down'}
                                          onChange={() => {
                                            handleChangeIconType(
                                              powerUnit.number,
                                              powerSwitch.number,
                                              'down'
                                            );
                                          }}
                                          size="small"
                                        />
                                      }
                                      label={
                                        <div className={classes.optionHolder}>
                                          <div>
                                            <ArrowDropDownIcon fontSize="large" />
                                          </div>
                                        </div>
                                      }
                                      labelPlacement="bottom"
                                    />
                                  </div>
                                </RadioGroup>
                              </div>
                            </Tooltip>

                            {powerSwitch.shutter_mode !== 1 &&
                              !shouldSummaryChecked && (
                                <div className={classes.shutterMode} />
                              )}
                            <SwitchCheckbox
                              style={{
                                display:
                                  // eslint-disable-next-line
                                  powerSwitch.shutter_mode == 1 ||
                                  shouldSummaryChecked
                                    ? 'inherit'
                                    : 'none',
                              }}
                              color="#000"
                              label="ワンショットモード"
                              {...{
                                // eslint-disable-next-line
                                checked: powerSwitch.shutter_mode == 1,
                                name: `${switchName}.shutter_mode`,
                                inputRef: register({}),
                                onClick: () =>
                                  handleChangeShutterMode(
                                    powerUnit.number,
                                    powerSwitch.number
                                  ),
                              }}
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.blankPanel}>
            <div>
              <SwitchCheckbox
                label="異常検出機能を使用する"
                {...{
                  checked: !!moduleWith.leakage_check,
                  name: 'leakage_check',
                  inputRef: register({}),
                  onClick: () => {
                    setModuleWith((prev) => {
                      const nextStatus = !moduleWith.leakage_check;
                      const nextState = {
                        ...prev,
                        leakage_check: nextStatus,
                      };

                      if (!nextStatus) {
                        nextState.unitsByNumber = Object.values(
                          moduleWith.unitsByNumber
                        ).reduce((units: any, unit) => {
                          units[unit.number] =
                            moduleWith.unitsByNumber[unit.number];

                          units[unit.number].switchesByNumber = Object.values(
                            unit.switchesByNumber
                          ).reduce((sws: any, sw) => {
                            sws[sw.number] = unit.switchesByNumber[sw.number];
                            sws[sw.number].leakage_check = false;
                            return sws;
                          }, {});

                          return units;
                        }, {});
                      }
                      return nextState;
                    });
                  },
                }}
              />
              {!!moduleWith.leakage_check && (
                <div className={classes.leakageCheckOptions}>
                  <CustomButton
                    onClick={() => {
                      setModuleWith((prev) => {
                        return {
                          ...prev,
                          unitsByNumber: Object.values(
                            moduleWith.unitsByNumber
                          ).reduce((units: any, unit) => {
                            units[unit.number] =
                              moduleWith.unitsByNumber[unit.number];

                            units[unit.number].switchesByNumber = Object.values(
                              unit.switchesByNumber
                            ).reduce((sws: any, sw) => {
                              sws[sw.number] = unit.switchesByNumber[sw.number];
                              sws[sw.number].leakage_check = true;
                              return sws;
                            }, {});

                            return units;
                          }, {}),
                        };
                      });
                    }}
                    color="grey"
                  >
                    全選択
                  </CustomButton>
                  <CustomButton
                    onClick={() => {
                      setModuleWith((prev) => {
                        return {
                          ...prev,
                          unitsByNumber: Object.values(
                            moduleWith.unitsByNumber
                          ).reduce((units: any, unit) => {
                            units[unit.number] =
                              moduleWith.unitsByNumber[unit.number];

                            units[unit.number].switchesByNumber = Object.values(
                              unit.switchesByNumber
                            ).reduce((sws: any, sw) => {
                              sws[sw.number] = unit.switchesByNumber[sw.number];
                              sws[sw.number].leakage_check = false;
                              return sws;
                            }, {});

                            return units;
                          }, {}),
                        };
                      });
                    }}
                    color="grey"
                  >
                    全解除
                  </CustomButton>
                </div>
              )}
              <div className={classes.description}>
                ※チェックを入れると子機との通信異常を検出しヘッダでのお知らせとメール通知いたします
              </div>
              {/*<div style={{ marginTop: 20 }}>*/}
              {/*  <FormControlLabel*/}
              {/*    className={classes.earthquakeSettings}*/}
              {/*    control={*/}
              {/*      <SwitchCheckbox*/}
              {/*        {...{*/}
              {/*          checked: !!moduleWith.earthquake_warning_flg,*/}
              {/*          name: 'earthquake_warning_flg',*/}
              {/*          inputRef: register({}),*/}
              {/*          onClick: () => {*/}
              {/*            setModuleWith((prevState) => ({*/}
              {/*              ...prevState,*/}
              {/*              earthquake_warning_flg:*/}
              {/*                !prevState.earthquake_warning_flg,*/}
              {/*            }));*/}
              {/*          },*/}
              {/*        }}*/}
              {/*      />*/}
              {/*    }*/}
              {/*    label={*/}
              {/*      <Box component="div" fontSize={11}>*/}
              {/*        緊急地震速報*/}
              {/*      </Box>*/}
              {/*    }*/}
              {/*  />*/}
              {/*  <div style={{ width: '90%' }}>*/}
              {/*    <Select*/}
              {/*      ref={register({})}*/}
              {/*      name="earthquake_intensity"*/}
              {/*      readonly={!moduleWith.earthquake_warning_flg}*/}
              {/*      defaultValue={moduleWith.earthquake_intensity}*/}
              {/*      options={EarthquakeIntensities}*/}
              {/*      placeholder="予測震度を選択してください。"*/}
              {/*    />*/}
              {/*  </div>*/}
              {/*</div>*/}
              <SwitchCheckbox
                label="スケジュールアラート"
                {...{
                  checked: !!moduleWith.schedule_alert,
                  name: 'schedule_alert',
                  inputRef: register({}),
                  onClick: () => {
                    setModuleWith((prevState) => ({
                      ...prevState,
                      schedule_alert: !prevState.schedule_alert,
                    }));
                  },
                }}
              />
              <div className={classes.description}>
                ※スケジュールの実行が失敗した時のアラートメール機能を使用するかをオン/オフで切り替えができる
              </div>
              <div className={classes.noteHeading}>
                <SwitchCheckbox
                  label="ワンショットモード"
                  {...{
                    checked: shouldSummaryChecked,
                    onClick: () => {
                      const nextStatus = shouldSummaryChecked ? 0 : 1;

                      setModuleWith((prev) => {
                        return {
                          ...prev,
                          unitsByNumber: Object.values(
                            moduleWith.unitsByNumber
                          ).reduce((units: any, unit) => {
                            units[unit.number] =
                              moduleWith.unitsByNumber[unit.number];

                            units[unit.number].switchesByNumber =
                              Object.values(unit.switchesByNumber).reduce(
                                (sws: any, sw) => {
                                  sws[sw.number] =
                                    unit.switchesByNumber[sw.number];
                                  sws[sw.number].shutter_mode = sws[
                                    sw.number
                                  ].id
                                    ? nextStatus
                                    : 0;
                                  return sws;
                                },
                                {}
                              );

                            return units;
                          }, {}),
                        };
                      });
                    },
                  }}
                />
                </div>
                <div className={classes.description}>
                  ワンショットモードにチェックしたスイッチはONにした後すぐにOFFになります。
                </div>
                <SwitchCheckbox
                  {...{
                    checked: shouldSummaryLocked,
                    onClick: () => {
                      const nextStatus = shouldSummaryLocked ? 0 : 1;

                      setModuleWith((prev) => {
                        return {
                          ...prev,
                          unitsByNumber: Object.values(
                            moduleWith.unitsByNumber
                          ).reduce((units: any, unit) => {
                            units[unit.number] =
                              moduleWith.unitsByNumber[unit.number];

                            units[unit.number].switchesByNumber = Object.values(
                              unit.switchesByNumber
                            ).reduce((sws: any, sw) => {
                              sws[sw.number] = unit.switchesByNumber[sw.number];
                              sws[sw.number].is_lock = nextStatus;
                              return sws;
                            }, {});

                            return units;
                          }, {}),
                        };
                      });
                    },
                  }}
                  label="指令盤をロックする"
                />
                <div className={classes.description}>
                  スイッチをロックします。
                  ロックされたスイッチはボタンを押しても
                  オン/オフの切り替えがされません。
                </div>
            </div>
            <div
              className={[
                classes.footer,
                isMobile ? classes.isMobile : '',
              ].join(' ')}
            >
              <Modal
                title="接続状態テストの結果"
                width={530}
                trigger={
                  <CustomButton
                    className={classes.testConnectButton}
                    color="warning"
                  >
                    接続状態テスト
                  </CustomButton>
                }
              >
                <TestConnectPopup
                  units={Object.values(commModule.unitsByNumber) as Units[]}
                  siteId={commModule.site_id!}
                  deviceCode={commModule.device_code}
                />
              </Modal>

              {isMobile && (
                <CustomButton
                  onClick={onBack}
                  className={classes.backButton}
                  color="grey"
                >
                  戻る
                </CustomButton>
              )}
              <CustomButton
                type="submit"
                className={classes.backButton}
                color="success"
              >
                保存
              </CustomButton>
            </div>
          </div>
        </div>
      </form>
      <Drawer isOpen={isOpenCameraForm} onChange={setOpenCameraForm}>
        <CameraDrawer
          moduleId={+moduleId}
          onClose={() => setOpenCameraForm(false)}
        />
      </Drawer>
      <Drawer isOpen={isOpenRemark} onChange={setOpenRemark} >
        <>
          <span>＊記入後は備考欄を閉じて保存ボタンを押してください。</span>
          <RemarkTextField 
            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setRemark(e.target.value)} 
            remark={remark}
          />
          <CustomButton
            className={classes.remarkDrawCloseButton}
            color="primary"
            onClick={() => setOpenRemark(false)}
          >
            備考欄を閉じる
          </CustomButton>
        </>
      </Drawer>
    </>
  );
};

export default B16ChannelSettings;
